/**
 * Paytr Payment Integration Service
 * 
 * Bu dosya Paytr ödeme altyapısı entegrasyonu için gerekli fonksiyonları içerir.
 * 
 * ÖNEMLİ: Bu entegrasyon bir backend gerektirir. Paytr API anahtarları
 * sunucu tarafında saklanmalıdır, frontend'de asla doğrudan kullanılmamalıdır.
 * 
 * Kurulum:
 * 1. https://www.paytr.com/ adresinden merchant hesabınızı oluşturun
 * 2. Merchant ID ve Merchant Key bilgilerinizi admin panelinden girin
 * 3. Backend API endpoint'lerinizi yapılandırın
 */

export interface PaytrConfig {
  merchantId: string;
  merchantKey: string;
  merchantSalt: string;
  successUrl: string;
  failUrl: string;
  callbackUrl: string;
}

export interface PaytrPaymentRequest {
  orderId: string;
  amount: number; // Kuruş cinsinden (ör: 100.00 TL = 10000)
  currency: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  shippingAddress: string;
  billingAddress: string;
  items: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface PaytrPaymentResponse {
  success: boolean;
  token?: string;
  iframeUrl?: string;
  error?: string;
}

export interface PaytrCallbackData {
  status: string;
  order_id: string;
  total_amount: string;
  hash: string;
}

class PaytrService {
  private config: PaytrConfig | null = null;

  /**
   * Paytr yapılandırmasını ayarla
   */
  setConfig(config: PaytrConfig) {
    this.config = config;
  }

  /**
   * Backend API üzerinden ödeme başlat
   * 
   * NOT: Bu fonksiyon backend'inizi çağırır.
   * Backend, Paytr API'yi güvenli bir şekilde çağırmalıdır.
   */
  async initiatePayment(paymentData: PaytrPaymentRequest): Promise<PaytrPaymentResponse> {
    try {
      // Backend API endpoint'ini çağır
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/paytr/create-payment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        throw new Error('Ödeme başlatılamadı');
      }

      const data = await response.json();
      return {
        success: true,
        token: data.token,
        iframeUrl: data.iframe_url,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Ödeme sonucunu doğrula
   * 
   * Backend callback endpoint'inden çağrılır.
   */
  async verifyPayment(callbackData: PaytrCallbackData): Promise<boolean> {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/paytr/verify-payment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(callbackData),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Ödeme doğrulama hatası:', error);
      return false;
    }
  }

  /**
   * Tutar formatı (Paytr kuruş cinsinden ister)
   */
  formatAmountForPaytr(amount: number): number {
    return Math.round(amount * 100);
  }

  /**
   * Tarih formatı (AA/YY -> Paytr formatı)
   */
  formatExpiryForPaytr(expiry: string): string {
    const [month, year] = expiry.split('/');
    return `${month}/${year}`;
  }
}

// Singleton instance
export const paytrService = new PaytrService();

/**
 * Paytr güvenli ödeme iframe adresini oluşturur.
 *
 * Not: Paytr'ın gerçek entegrasyonu özel bir JS kütüphanesi gerektirmez —
 * doğrudan bu adrese işaret eden düz bir <iframe src="..."> yeterlidir.
 */
export function getPaytrIframeUrl(token: string): string {
  return `https://www.paytr.com/odeme/guvenli/${token}`;
}

/**
 * Paytr callback handler
 * 
 * Ödeme sonrası callback'i işler.
 */
export function handlePaytrCallback(
  callbackData: PaytrCallbackData,
  onSuccess: (orderId: string) => void,
  onFail: (orderId: string, error: string) => void
): void {
  if (callbackData.status === 'success') {
    onSuccess(callbackData.order_id);
  } else {
    onFail(callbackData.order_id, 'Ödeme başarısız oldu');
  }
}

export default paytrService;
